import React, { useState, useCallback, useRef, useEffect } from 'react';
import { uppercaseLabel } from '../styles/typography';
import {
    Button,
    Box,
    Typography,
    CircularProgress,
    Snackbar,
    Alert,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    IconButton,
    Popover,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { frostedPanelBg, popoverElevationShadow } from '../styles/surfaceSx';
import Papa from 'papaparse';
import useImportColumnConfirmation from '../hooks/useImportColumnConfirmation';
import { readFirstWorksheet, readImportMatrix } from '../utils/importColumnReview';
import { useImportFileTransfer } from '../context/ImportFileTransferContext';
import { useSecurityOperationContext } from '../context/SecurityOperationContext';
import * as XLSX from 'xlsx';
import { downloadUploadTemplate } from '../utils/uploadTemplateDownload';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    watchlistInsetPanelSx,
    watchlistPopoverPaperSx,
    watchlistSecondaryControlSx,
    watchlistSemanticFrameSx,
} from '../styles/watchlistMsiStyles';
import {
    FILE_RESULTS_THAT_CLEAR_SELECTION,
    FILE_UPLOAD_RESULT_KINDS,
} from './fileUploadResult';

function DynamicTable({ data }) {
    if (!Array.isArray(data) || data.length === 0) return null;
    const headers = data[0];
    const rows = data.slice(1);

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
                <tr>
                    {headers.map((h, idx) => (
                        <th key={idx} style={{ border: '1px solid #ccc', padding: '4px', background: '#f5f5f5' }}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, rIdx) => (
                    <tr key={rIdx}>
                        {row.map((cell, cIdx) => (
                            <td key={cIdx} style={{ border: '1px solid #eee', padding: '4px' }}>{cell}</td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
export default function FileUploader({
    uploaderKey,
    onFileUpload,
    requiredColumns,
    labelName = 'Portfolio',
    helperText = '',
    infoPopoverContent = null,
    compact = false,
    compactVariant = 'inline',
    showTitle = true,
    onInteraction,
    onColumnReviewChange,
    reviewColumns,
}) {
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [previewData, setPreviewData] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [infoAnchorEl, setInfoAnchorEl] = useState(null);
    const [compactAnchorEl, setCompactAnchorEl] = useState(null);

    const fileInputRef = useRef(null);
    // Keep the authoritative File outside render timing. After a rejected
    // upload the prior file remains selected; a new chooser selection must be
    // visible to Upload immediately, even before React commits the next render.
    const selectedFileRef = useRef(null);
    const selectionGenerationRef = useRef(0);
    const { sessionScopeToken } = useSecurityOperationContext();
    const { pending, claim } = useImportFileTransfer();
    const { confirmColumns, columnDialog } = useImportColumnConfirmation('watchlist');
    useEffect(() => {
        selectionGenerationRef.current += 1;
        selectedFileRef.current = null;
        setFile(null);
        setFileName('');
        setPreviewData(null);
        return () => { selectionGenerationRef.current += 1; };
    }, [sessionScopeToken]);
    const markInteraction = useCallback(() => {
        onInteraction?.();
    }, [onInteraction]);

    const parseFileForPreview = useCallback((selectedFile) => {
        const selectionGeneration = selectionGenerationRef.current + 1;
        selectionGenerationRef.current = selectionGeneration;
        const ext = selectedFile.name.split('.').pop().toLowerCase();
        if (!['csv', 'xlsx', 'xls'].includes(ext)) {
            setError('Upload a valid CSV or Excel file.');
            selectedFileRef.current = null;
            setFile(null);
            setFileName('');
            setPreviewData(null);
            return;
        }
        selectedFileRef.current = selectedFile;
        setFile(selectedFile);
        setFileName(selectedFile.name);
        setError('');
        setSuccess('');
        setPreviewData(null);

        const reader = new FileReader();
        reader.onload = (e) => {
            if (selectionGenerationRef.current !== selectionGeneration) return;
            const buf = e.target.result;
            if (ext === 'csv') {
                const text = new TextDecoder('utf-8').decode(buf);
                const { data } = Papa.parse(text, { header: false, skipEmptyLines: true });
                setPreviewData(data.slice(0, 5));
            } else {
                try {
                    const ws = readFirstWorksheet(buf);
                    const arr = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
                    setPreviewData(arr.slice(0, 6));
                } catch {
                    setError('Failed to parse Excel preview.');
                }
            }
        };
        reader.readAsArrayBuffer(selectedFile);
    }, []);

    const handleFileChange = (e) => {
        if (uploading) return;
        markInteraction();
        const f = e.target.files[0];
        if (f) parseFileForPreview(f);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (uploading) return;
        markInteraction();
        const f = e.dataTransfer.files[0];
        if (f) parseFileForPreview(f);
    };
    const prepareFileData = async (file) => {
        if (!file) throw new Error('No file selected.');
        const ext = file.name.split('.').pop().toLowerCase();
        if (!['csv', 'xlsx', 'xls'].includes(ext)) throw new Error('Upload a valid CSV or Excel file.');

        const buf = await file.arrayBuffer();
        let matrix = [];
        let parsedRows = [];
        let headerRow = [];

        if (ext === 'csv') {
            const text = new TextDecoder('utf-8').decode(buf);
            const result = Papa.parse(text, { header: false, skipEmptyLines: false });
            if (result.errors.some((error) => error?.code !== 'UndetectableDelimiter')) {
                throw new Error('CSV parse error. Please check file format.');
            }
            matrix = result.data;
            const legacyResult = Papa.parse(text, { header: true, skipEmptyLines: true });
            if (legacyResult.errors.some((error) => error?.code !== 'UndetectableDelimiter')) {
                throw new Error('CSV parse error. Please check file format.');
            }
            headerRow = legacyResult.meta.fields || [];
            parsedRows = legacyResult.data;
        } else {
            const ws = readFirstWorksheet(buf);
            matrix = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: true });
            const legacySheetData = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
            headerRow = (legacySheetData[0] || []).map((column) => String(column).trim());
            parsedRows = legacySheetData.slice(1).map((rowArray) => {
                const row = {};
                rowArray.forEach((value, index) => {
                    row[headerRow[index]] = value;
                });
                return row;
            });
        }

        const normalizedHeaders = headerRow.map((header) => String(header).toLowerCase());
        const missingRequired = requiredColumns.filter(
            (required) => !normalizedHeaders.includes(String(required).toLowerCase()),
        );
        if (missingRequired.length) {
            throw new Error(`Missing required columns: ${missingRequired.join(', ')}`);
        }

        return {
            file,
            matrix,
            parsedRows,
            headerRow,
            ext
        };
    };

    const handleUpload = async (incomingFile = selectedFileRef.current) => {
        markInteraction();
        const selectedFile = incomingFile;
        const selectionGeneration = selectionGenerationRef.current;
        if (!selectedFile) {
            setError('No file selected.');
            return;
        }
        setUploading(true);
        setError('');
        setSuccess('');
        try {
            let prepared;
            if (uploaderKey === 'watchlist') {
                onColumnReviewChange?.(true);
                const matrix = await readImportMatrix(selectedFile);
                if (selectionGenerationRef.current !== selectionGeneration) return;
                // The server checks the columns; the user confirms them here.
                const review = await reviewColumns(matrix);
                if (selectionGenerationRef.current !== selectionGeneration) return;
                const columnSelection = await confirmColumns({
                    review,
                    sourceLabel: selectedFile.name,
                    file: selectedFile,
                });
                if (!columnSelection) return;
                prepared = { file: selectedFile, matrix, columnSelection };
            } else {
                prepared = await prepareFileData(selectedFile);
            }
            if (selectionGenerationRef.current !== selectionGeneration) return;
            const uploadResult = await onFileUpload(prepared);
            if (
                selectionGenerationRef.current !== selectionGeneration
                || selectedFileRef.current !== selectedFile
            ) {
                return;
            }
            const resultKind = uploadResult?.kind;
            if (!Object.values(FILE_UPLOAD_RESULT_KINDS).includes(resultKind)) {
                throw new Error('Uploader returned an unknown file result. The selected file was retained.');
            }
            if (!FILE_RESULTS_THAT_CLEAR_SELECTION.has(resultKind)) {
                throw new Error(uploadResult.message || (
                    resultKind === FILE_UPLOAD_RESULT_KINDS.REJECTED
                        ? 'The selected file was rejected.'
                        : 'Failed to process the file on the server.'
                ));
            }
            setSuccess(
                uploadResult.message
                || (resultKind === FILE_UPLOAD_RESULT_KINDS.STAGED
                    ? 'Upload staged successfully.'
                    : resultKind === FILE_UPLOAD_RESULT_KINDS.UNCHANGED
                        ? 'The upload matches the current data; no changes were needed.'
                        : 'Upload and processing successful.'),
            );
            selectedFileRef.current = null;
            selectionGenerationRef.current += 1;
            setFile(null);
            setFileName('');
            setPreviewData(null);
            setCompactAnchorEl(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err) {
            if (
                selectionGenerationRef.current === selectionGeneration
                && selectedFileRef.current === selectedFile
            ) {
                setError(err instanceof Error ? err.message : String(err));
            }
        } finally {
            onColumnReviewChange?.(false);
            setUploading(false);
        }
    };
    useEffect(() => {
        if (uploaderKey !== 'watchlist' || pending?.destination !== 'watchlist') return;
        const transferred = claim(pending);
        if (!transferred) return;
        parseFileForPreview(transferred);
        handleUpload(transferred);
    }, [uploaderKey, pending, claim, parseFileForPreview]);

    const handlePreviewOpen = () => {
        markInteraction();
        setPreviewOpen(true);
        setCompactAnchorEl(null);
    };
    const handlePreviewClose = () => setPreviewOpen(false);
    const handleCancel = () => {
        if (uploading) return;
        selectionGenerationRef.current += 1;
        selectedFileRef.current = null;
        setFile(null);
        setFileName('');
        setPreviewData(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };
    const compactPopoverOpen = Boolean(compactAnchorEl);
    const compactDropdown = compact && compactVariant === 'dropdown';

    const closeCompactPopover = () => {
        setCompactAnchorEl(null);
        setInfoAnchorEl(null);
    };

    const handleCompactTriggerClick = (event) => {
        markInteraction();
        const nextAnchor = event.currentTarget;
        const sameAnchor = compactAnchorEl === nextAnchor;

        if (compactPopoverOpen && sameAnchor) {
            closeCompactPopover();
            return;
        }

        setCompactAnchorEl(nextAnchor);
    };

    const compactTriggerButtonSx = (isActive) => [{
        minHeight: 28,
        px: 0,
        pl: 1.05,
        pr: 0.35,
        borderRadius: 999,
        border: '1px solid',
        borderColor: (theme) => alpha(
            isActive ? theme.palette.primary.main : theme.palette.divider,
            theme.palette.mode === 'dark'
                ? (isActive ? 0.44 : 0.42)
                : (isActive ? 0.3 : 0.82),
        ),
        backgroundColor: (theme) => isActive
            ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08)
            : alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.28 : 0.88),
        color: 'text.primary',
        fontSize: '0.66rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        boxShadow: 'none',
        '& .MuiButton-endIcon': {
            ml: 0.6,
            mr: 0,
            pl: 0.5,
            borderLeft: '1px solid',
            borderColor: (theme) => alpha(
                isActive ? theme.palette.primary.main : theme.palette.divider,
                theme.palette.mode === 'dark'
                    ? (isActive ? 0.34 : 0.34)
                    : (isActive ? 0.26 : 0.72),
            ),
            color: 'text.secondary',
        },
        '&:hover': {
            borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.48 : 0.36),
            backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
            boxShadow: 'none',
        },
    }, watchlistSecondaryControlSx];
    
    function handleDownloadTemplate(e, format) {
        e.preventDefault();
        downloadUploadTemplate(uploaderKey, format);
    }
    const compactControls = (
        <Box display="flex" alignItems="center" gap={0.65} sx={{ mb: 0.2, flexWrap: 'wrap' }}>
            <Tooltip title="Choose file">
                <span>
                    <IconButton
                        aria-label="Choose file"
                        size="small"
                        color="primary"
                        disabled={uploading}
                        onClick={() => {
                            markInteraction();
                            // Clearing the native value makes selecting the
                            // same path again emit onChange after a failure.
                            if (fileInputRef.current) fileInputRef.current.value = '';
                            fileInputRef.current?.click();
                        }}
                        sx={[{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }, watchlistSecondaryControlSx]}
                    >
                        <InsertDriveFileOutlinedIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Upload">
                <span>
                    <IconButton
                        aria-label="Upload"
                        size="small"
                        color="primary"
                        onClick={() => handleUpload()}
                        disabled={uploading || !file}
                        sx={[{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }, watchlistSecondaryControlSx]}
                    >
                        {uploading ? <CircularProgress size={16} /> : <CloudUploadOutlinedIcon fontSize="small" />}
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Preview">
                <span>
                    <IconButton
                        aria-label="Preview"
                        size="small"
                        color="primary"
                        onClick={handlePreviewOpen}
                        disabled={uploading || !file}
                        sx={[{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }, watchlistSecondaryControlSx]}
                    >
                        <PreviewOutlinedIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={fileName || 'Drop file here'}>
                <Box
                    sx={[
                        {
                            border: '1px dashed',
                            borderColor: dragOver ? 'primary.main' : 'grey.400',
                            borderRadius: 999,
                            width: fileName ? 158 : 120,
                            height: 30,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            cursor: 'pointer',
                            bgcolor: dragOver ? 'action.hover' : 'rgba(0,0,0,0.04)',
                            transition: 'background-color 0.2s',
                            px: 1,
                            gap: 0.75,
                        },
                        watchlistSemanticFrameSx(dragOver ? 'info' : 'neutral'),
                    ]}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    aria-disabled={uploading}
                    onClick={() => {
                        if (uploading) return;
                        markInteraction();
                        if (fileInputRef.current) fileInputRef.current.value = '';
                        fileInputRef.current?.click();
                    }}
                >
                    <UploadFileOutlinedIcon fontSize="small" color={dragOver ? 'primary' : 'action'} />
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: '0.66rem',
                            color: fileName ? 'text.primary' : 'text.secondary',
                            lineHeight: 1.2,
                        }}
                        noWrap
                    >
                        {fileName || 'Drop file'}
                    </Typography>
                </Box>
            </Tooltip>

            <Tooltip title="Cancel">
                <span>
                    <IconButton
                        aria-label="Cancel"
                        size="small"
                        color="error"
                        onClick={handleCancel}
                        disabled={uploading || !file}
                        sx={[{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }, watchlistSecondaryControlSx]}
                    >
                        <CloseOutlinedIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Import requirements">
                <IconButton
                    aria-label="Import requirements"
                    size="small"
                    color="default"
                    onClick={(event) => setInfoAnchorEl(event.currentTarget)}
                    sx={[{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }, watchlistSecondaryControlSx]}
                >
                    <InfoOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );

    return (
         <>
            {columnDialog}
            {showTitle && (
                <Typography
                    variant="subtitle1"
                    sx={{
                        mb: compact ? 0.5 : 1,
                        width: '100%',
                    }}
                >
                    Upload {labelName}
                </Typography>
            )}
        <Box sx={{ p: 0, width: compact ? 'auto' : '100%' }}>
            {!compact && (
                <Typography variant="body2" gutterBottom>
                    Required columns: {requiredColumns.join(', ')}.
                    {helperText ? ` ${helperText} ` : ' '}
                    Download Sample:&nbsp;
                    <Link onClick={e => handleDownloadTemplate(e, 'csv')} sx={{ cursor: 'pointer' }}>CSV</Link> or&nbsp;
                    <Link onClick={e => handleDownloadTemplate(e, 'excel')} sx={{ cursor: 'pointer' }}>Excel</Link>.
                </Typography>
            )}

            {compact ? (
                compactDropdown ? (
                    <>
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleCompactTriggerClick}
                            endIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        fontSize: 16,
                                        transform: compactPopoverOpen ? 'rotate(180deg)' : 'none',
                                        transition: 'transform 160ms ease',
                                    }}
                                />
                            }
                            sx={compactTriggerButtonSx(compactPopoverOpen)}
                        >
                            Upload
                        </Button>
                        <Popover
                            open={compactPopoverOpen}
                            anchorEl={compactAnchorEl}
                            onClose={closeCompactPopover}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                                sx: [
                                    {
                                    mt: 0.75,
                                    width: { xs: 'min(92vw, 360px)', sm: 360 },
                                    maxWidth: 'calc(100vw - 32px)',
                                    p: 0.8,
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: popoverElevationShadow,
                                    },
                                    watchlistPopoverPaperSx,
                                ],
                            }}
                        >
                            <Box
                                sx={[
                                    {
                                        p: 1,
                                        borderRadius: 2.5,
                                        backgroundColor: (theme) => frostedPanelBg(theme),
                                    },
                                    watchlistInsetPanelSx,
                                ]}
                            >
                                {compactControls}
                            </Box>
                        </Popover>
                    </>
                ) : compactControls
            ) : (
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <Box
                        sx={[
                            {
                                border: '2px dashed',
                                borderColor: dragOver ? 'primary.main' : 'grey.400',
                                borderRadius: 1,
                                width: 230,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                bgcolor: dragOver ? 'action.hover' : 'transparent',
                                transition: 'background-color 0.2s',
                                px: 1,
                            },
                            watchlistSemanticFrameSx(dragOver ? 'info' : 'neutral'),
                        ]}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => {
                            if (uploading) return;
                            markInteraction();
                            if (fileInputRef.current) fileInputRef.current.value = '';
                            fileInputRef.current?.click();
                        }}
                        aria-disabled={uploading}
                    >
                        <Typography variant="body2" color={dragOver ? 'primary' : 'textPrimary'}>
                            {fileName || 'Drop file here'}
                        </Typography>
                    </Box>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            if (uploading) return;
                            markInteraction();
                            if (fileInputRef.current) fileInputRef.current.value = '';
                            fileInputRef.current?.click();
                        }}
                        disabled={uploading}
                    >
                        Choose File
                    </Button>

                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleUpload()}
                        disabled={uploading || !file}
                    >
                        {uploading ? <CircularProgress size={16} /> : 'Upload'}
                    </Button>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handlePreviewOpen}
                        disabled={uploading || !file}
                    >
                        Preview
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCancel}
                        disabled={uploading || !file}
                    >
                        Cancel
                    </Button>
                </Box>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                hidden
                disabled={uploading}
                onChange={handleFileChange}
            />

            {compact && (
                <Popover
                    open={Boolean(infoAnchorEl)}
                    anchorEl={infoAnchorEl}
                    onClose={() => setInfoAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{ sx: [{ p: 1.25, maxWidth: 320, borderRadius: 2 }, watchlistPopoverPaperSx] }}
                >
                    {infoPopoverContent ? (
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            {infoPopoverContent}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                            Upload a CSV or Excel file with required columns <strong>{requiredColumns.join(', ')}</strong>.
                            {helperText ? ` ${helperText}` : ''}
                        </Typography>
                    )}
                    <Box display="flex" gap={0.75} sx={{ mt: 1 }}>
                        <Link
                            onClick={(event) => {
                                handleDownloadTemplate(event, 'csv');
                                setInfoAnchorEl(null);
                            }}
                            sx={{ cursor: 'pointer', ...uppercaseLabel({ fontSize: '0.72rem' }) }}
                        >
                            CSV Template
                        </Link>
                        <Link
                            onClick={(event) => {
                                handleDownloadTemplate(event, 'excel');
                                setInfoAnchorEl(null);
                            }}
                            sx={{ cursor: 'pointer', ...uppercaseLabel({ fontSize: '0.72rem' }) }}
                        >
                            Excel Template
                        </Link>
                    </Box>
                </Popover>
            )}

            <Dialog 
                open={previewOpen} 
                onClose={handlePreviewClose}     
                sx={{ '& .MuiDialog-paper': { width: 700, maxWidth: '60vw' } }}
>
                <DialogTitle>Preview</DialogTitle>
                <DialogContent dividers>
                    <Box
                        component="pre"
                        sx={{
                            fontSize: '0.75rem',
                            fontFamily: 'monospace',
                            m: 0,
                            maxHeight: 300,
                            overflow: 'auto',
                        }}
                    >
                        {Array.isArray(previewData)
                            ? <DynamicTable data={previewData} />
                            : JSON.stringify(previewData, null, 2)}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button size="small" onClick={handlePreviewClose}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {error && (
                <Snackbar open autoHideDuration={6000} onClose={() => setError('')}>
                    <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                </Snackbar>
            )}
            {success && (
                <Snackbar open autoHideDuration={6000} onClose={() => setSuccess('')}>
                    <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                </Snackbar>
            )}
        </Box>
        </>
    );
}
